/** The HTTP side of the server: crews, my worlds, guests. Errors carry the server's code for i18n. */
import type { CrewInfo, GuestResponse, WorldsResponse } from '@bramvasterd/terrella-protocol';

import { apiBase } from './url';

export class ApiFailure extends Error {
  constructor(
    readonly code: string,
    readonly status: number,
    readonly params: Record<string, unknown> = {},
  ) {
    super(`API ${status}: ${code}`);
  }
}

/** A valid access token for the player, refreshed when `fresh` asks for it; null when signed out. */
export type TokenProvider = (fresh?: boolean) => Promise<string | null>;

let provider: TokenProvider = () => Promise.resolve(null);

export function configureApi(options: { token: TokenProvider }): void {
  provider = options.token;
}

async function call<T>(
  method: string,
  path: string,
  body?: unknown,
  auth = true,
  retried = false,
): Promise<T> {
  const headers: Record<string, string> = { accept: 'application/json' };
  if (body !== undefined) headers['content-type'] = 'application/json';
  if (auth) {
    const token = await provider(retried);
    if (!token) throw new ApiFailure('unauthorized', 401);
    headers['authorization'] = `Bearer ${token}`;
  }
  let response: Response;
  try {
    response = await fetch(`${apiBase()}${path}`, {
      method,
      headers,
      body: body === undefined ? null : JSON.stringify(body),
    });
  } catch {
    throw new ApiFailure('network', 0);
  }
  if (response.status === 401 && auth && !retried) return call<T>(method, path, body, auth, true);
  if (!response.ok) {
    let code = 'unknown';
    let params: Record<string, unknown> = {};
    try {
      const { error } = (await response.json()) as {
        error: { code: string } & Record<string, unknown>;
      };
      const { code: c, ...rest } = error;
      code = c;
      params = rest;
    } catch {
      /* no body */
    }
    throw new ApiFailure(code, response.status, params);
  }
  return (await response.json()) as T;
}

export const listCrews = async (): Promise<CrewInfo[]> =>
  (await call<{ crews: CrewInfo[] }>('GET', '/crews', undefined, false)).crews;

export const createCrew = async (name: string): Promise<CrewInfo> =>
  (await call<{ crew: CrewInfo }>('POST', '/crews', { name })).crew;

export const joinCrew = async (id: string): Promise<CrewInfo> =>
  (await call<{ crew: CrewInfo }>('POST', `/crews/${encodeURIComponent(id)}/join`)).crew;

/** My worlds, the last played first (a guest has none). */
export const myWorlds = async (): Promise<CrewInfo[]> =>
  (await call<WorldsResponse>('GET', '/me/worlds')).worlds;

/** A throwaway account for someone who opened `world`'s link without signing in. */
export const createGuest = (world: string, lang: string): Promise<GuestResponse> =>
  call<GuestResponse>('POST', '/guest', { world, lang }, false);

export const leaveCrew = async (id: string): Promise<void> => {
  await call('POST', `/crews/${encodeURIComponent(id)}/leave`);
};

export const health = (): Promise<{ ok: boolean; rules: number; protocol: number }> =>
  call('GET', '/health', undefined, false);
